import { resolveMessagingTemplateByCode } from "src-next/modules/crm-clinical/communications/helpers/resolveMessagingTemplateByCode";
import type { MessagingTemplate } from "src-next/modules/crm-clinical/messaging/types";
import type { MessageTemplate } from "src-next/modules/crm-clinical/messageTemplates/types";

import type { PostAttendanceFollowUpType } from "../hooks/usePostAttendanceFollowUpSendDialog";

export const POST_ATTENDANCE_TEMPLATE_CODE: Record<
  PostAttendanceFollowUpType,
  string
> = {
  medication: "flow_medication",
  exam: "flow_exame",
};

/** Substitui variáveis `{{nome}}` pelo valor informado no mapa. */
export function substituteTemplateVariables(
  templateText: string,
  variables: Record<string, string>,
): string {
  return templateText.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => {
    const value = variables[key];
    return value != null && String(value).trim() !== ""
      ? String(value)
      : `{{${key}}}`;
  });
}

/** Extrai texto legível da descrição de exame (pode ser string ou nós React). */
export function resolveExamDescriptionText(data: any): string {
  const description = data?.description;

  if (typeof description === "string") {
    return description.trim();
  }

  if (Array.isArray(description)) {
    return description
      .map((item) => {
        if (typeof item === "string") return item.trim();
        const childText = item?.props?.children;
        return typeof childText === "string" ? childText.trim() : "";
      })
      .filter(Boolean)
      .join(", ");
  }

  const nestedText = description?.[0]?.props?.children;
  if (typeof nestedText === "string") {
    return nestedText.trim();
  }

  return (
    data?.serviceRequest?.code?.concept?.coding?.[0]?.display?.trim() ?? ""
  );
}

export function buildPostAttendanceFollowUpVariables(params: {
  type: PostAttendanceFollowUpType;
  data: any;
  patientData?: { name?: string };
  organizationName?: string;
}): Record<string, string> {
  const patientName = String(params.patientData?.name ?? "").trim();
  const organizationName = String(params.organizationName ?? "").trim();

  if (params.type === "medication") {
    return {
      nome_paciente: patientName,
      data: String(params.data?.date ?? "").trim(),
      nome_profissional: String(params.data?.practitioner ?? "").trim(),
      especialidade: String(params.data?.specialty ?? "").trim(),
      nome_medicamento: String(params.data?.description ?? "").trim(),
    };
  }

  return {
    nome_paciente: patientName,
    nome_clinica: organizationName,
    nome_exame: resolveExamDescriptionText(params.data),
    data_exame: String(params.data?.date ?? "").trim(),
    orientacao: String(params.data?.serviceRequest?.note?.[0]?.text ?? "").trim(),
    nome_profissional: String(params.data?.practitioner ?? "").trim(),
    link_resultado: String(params.data?.resultUrl ?? "").trim(),
  };
}

function mapMessageTemplateToMessaging(template: MessageTemplate): MessagingTemplate {
  return {
    id: template.id,
    name: template.name,
    preview: template.preview,
    header: template.header || undefined,
    status: template.status,
  };
}

/** Resolve o template WhatsApp (`flow_medication` / `flow_exame`) para envio programático. */
export function resolvePostAttendanceMessagingTemplate(
  templates: MessageTemplate[],
  type: PostAttendanceFollowUpType,
): MessagingTemplate | null {
  const template = resolveFollowUpMessageTemplate(templates, type);
  return template ? mapMessageTemplateToMessaging(template) : null;
}

function resolveFollowUpMessageTemplate(
  templates: MessageTemplate[],
  type: PostAttendanceFollowUpType,
): MessageTemplate | null {
  const templateCode = POST_ATTENDANCE_TEMPLATE_CODE[type];
  const messagingTemplates = templates.map(mapMessageTemplateToMessaging);
  const resolved = resolveMessagingTemplateByCode(
    messagingTemplates,
    templateCode,
  );

  if (!resolved) return null;

  return (
    templates.find(
      (template) => template.id === resolved.id || template.name === resolved.name,
    ) ?? null
  );
}

/** Prévia legada — fallback quando o template WhatsApp ainda não foi carregado. */
export function buildPostAttendanceFollowUpFallbackPreview(params: {
  type: PostAttendanceFollowUpType;
  data: any;
  patientData?: { name?: string };
}): string {
  const variables = buildPostAttendanceFollowUpVariables(params);

  if (params.type === "medication") {
    return `Olá, ${variables.nome_paciente.toUpperCase()}!

Você está recebendo esta mensagem porque durante seu atendimento no dia ${variables.data} com o(a) Dr.(a) ${variables.nome_profissional} te foi receitado o medicamento ${variables.nome_medicamento}.

A interação com seu médico é muito importante para seu cuidado. Informe o seu médico por aqui se você está tomando a sua medicação.`;
  }

  const specialty = String(params.data?.specialty ?? "").trim();

  return `Olá, ${variables.nome_paciente.toUpperCase()}!

Você está recebendo esta mensagem porque durante seu atendimento no dia ${variables.data_exame} com o(a) Dr.(a) ${variables.nome_profissional}${specialty ? ` - ${specialty}` : ""} foi solicitado o exame: ${variables.nome_exame}.

A interação com seu médico é muito importante para seu cuidado. Informe o seu médico por aqui sobre o seu exame.`;
}

export interface PostAttendanceFollowUpPreviewContent {
  header: string;
  body: string;
  hasButton: boolean;
  buttonText: string;
  usesTemplate: boolean;
}

export function buildPostAttendanceFollowUpPreview(params: {
  type: PostAttendanceFollowUpType;
  data: any;
  patientData?: { name?: string };
  organizationName?: string;
  templates?: MessageTemplate[];
}): PostAttendanceFollowUpPreviewContent {
  const variables = buildPostAttendanceFollowUpVariables(params);
  const template = resolveFollowUpMessageTemplate(
    params.templates ?? [],
    params.type,
  );

  if (!template) {
    return {
      header: "",
      body: buildPostAttendanceFollowUpFallbackPreview(params),
      hasButton: false,
      buttonText: "",
      usesTemplate: false,
    };
  }

  const header = substituteTemplateVariables(
    String(template.header ?? "").trim(),
    variables,
  );
  const body = substituteTemplateVariables(
    String(template.preview ?? "").trim(),
    variables,
  );

  const buttonText = String(template.buttonText ?? "").trim();

  return {
    header,
    body: body || buildPostAttendanceFollowUpFallbackPreview(params),
    hasButton: Boolean(buttonText),
    buttonText,
    usesTemplate: true,
  };
}
